import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppUtils } from '../../../core/config/app.utils';
import { FilesService } from '../../../core/services/files.service';
import { NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import { UserService } from '../../../core/services/user.service';
import { SETTINGS } from '../../../core/config/common.settings';
import { RolesService } from '../../../core/services/roles.service';
import { DepartmentsWiseCompanyService } from '../../../core/services/departments-wise-company.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ActionAlert } from '../../../core/dto/actionAlert';
import { NzMessageService } from 'ng-zorro-antd/message';

const ADD_EDIT_USER_IMAGE_LOADING = 'ADD_EDIT_USER_PROFILE_IMAGE_LOADING';

@Component({
  selector: 'app-add-edit-user',
  templateUrl: './add-edit-user.component.html',
  styleUrls: ['./add-edit-user.component.scss'],
})
export class AddEditUserComponent implements OnInit {
  breadcrumbs = [
    {
      title: 'Users',
      icon: 'user',
      route: '/admin/users',
    },
    {
      title: 'Add New User',
    },
  ];
  userForm: FormGroup | any;
  formErrors: any;
  statusValue = true;
  statusLoading = false;
  isAdd = false;
  userId: any;
  userInfo: any;

  imageUploading = false;
  uploadImageUrl: any;
  roleOptions: any[] = [];
  depOptions: any[] = [];
  loading = false;

  actionMessage = new ActionAlert();

  constructor(
    private fb: FormBuilder,
    private filesService: FilesService,
    private userService: UserService,
    private rolesService: RolesService,
    private departmentsService: DepartmentsWiseCompanyService,
    private router: Router,
    private route: ActivatedRoute,
    private message: NzMessageService,
  ) {
    this.formErrors = {
      firstname: {},
      lastname: {},
      mobile: {},
      staffNumber: {},
      email: {},
      department: {},
      role: {},
      active: {},
    };
  }

  ngOnInit() {
    this.userId = this.route.snapshot.paramMap.get('id');
    this.isAdd = !this.userId;

    if (this.userId) {
      this.breadcrumbs[1] = {
        title: 'Update User',
      };
    }

    this.userFormInit();

    this.loading = true;
    this.getOptions().then(() => {
      if (this.userId) {
        this.handleEditUserDataPopulation();
      } else {
        this.loading = false;
      }
    });

    this.filesService.fileUploadingSubject.subscribe((res: any) => {
      if (res.type === ADD_EDIT_USER_IMAGE_LOADING) {
        this.imageUploading = res.loading;
      }
    });
  }

  handleEditUserDataPopulation(): void {
    this.userService.getOneUserById(this.userId).then((res) => {
      this.userInfo = res;
      this.userForm.patchValue(res);
      this.uploadImageUrl = this.userInfo.imageUrl;
      this.loading = false;
    });
  }

  userFormInit() {
    this.userForm = this.fb.group({
      firstname: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(SETTINGS.REGEX.LETTERS.EXP),
        ]),
      ],
      lastname: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(SETTINGS.REGEX.LETTERS.EXP),
        ]),
      ],
      staffNumber: [
        '',
        Validators.compose([Validators.required, Validators.maxLength(15)]),
      ],
      mobile: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(SETTINGS.REGEX.PHONE.EXP),
        ]),
      ],
      email: [
        { value: '', disabled: !this.isAdd },
        [Validators.required, Validators.email],
      ],
      department: [[], Validators.required],
      role: ['', Validators.required],
      active: [true, Validators.required],
    });
    this.userForm.valueChanges.subscribe(() => {
      this.formErrors = AppUtils.getFormErrors(this.userForm, this.formErrors);
    });
  }

  customUploadReq = (item: NzUploadXHRArgs) =>
    this.filesService
      .postFile(item.file, ADD_EDIT_USER_IMAGE_LOADING)
      .subscribe((res) => {
        this.filesService.fileUploadingSubject.next({
          type: ADD_EDIT_USER_IMAGE_LOADING,
          loading: false,
        });
        const data = res as any;
        this.uploadImageUrl = data.url;
        this.message.success('Image has been uploaded!');
      });

  removeProfileImg() {
    this.uploadImageUrl = null;
  }

  async createUser(payload: any): Promise<void> {
    try {
      await this.userService.createUser(payload);
      this.message.success('New user has been added!');
      this.router.navigate(['/admin/users']);
    } catch (e: any) {
      if (e.emailCheck || e.staffIdCheck) {
        this.actionMessage = new ActionAlert(true, e.message, 'error');
      } else {
        this.message.error('New user has not been added!');
      }
    }
  }

  async updateUser(payload: any): Promise<void> {
    try {
      await this.userService.updateUser(payload);
      this.message.success('User has been updated!');
      this.router.navigate(['/admin/users']);
    } catch (e: any) {
      if (e.emailCheck || e.staffIdCheck) {
        this.actionMessage = new ActionAlert(true, e.message, 'error');
      } else {
        this.message.error('User has not been updated!');
      }
    }
  }

  submit(): void {
    const formData = this.userForm.getRawValue();
    const payload = {
      ...formData,
      imageUrl: this.uploadImageUrl,
    };
    if (this.userId) {
      this.updateUser({ ...payload, _id: this.userId });
    } else {
      this.createUser(payload);
    }
  }

  async getOptions(): Promise<any> {
    this.departmentsService.departmentsOnChange.subscribe((res: any) => {
      this.depOptions = res;
    });
    this.roleOptions = await this.rolesService.getRolesForOptions();
  }

  wordCapitalize(word: string): string {
    const lower = word.toLowerCase();
    return word.charAt(0).toUpperCase() + lower.slice(1);
  }
}
